import type {
  AppConfigStatus,
  CommentPreview,
  DashboardStats,
  Finding,
  FindingStatus,
  Paginated,
  PublicInfo,
  PublishResult,
  PullRequestSummary,
  Review,
  ReviewDepth,
  ReviewListParams,
  ReviewSummary,
  User,
} from '../types/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryable = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the PR Pilot server. Is the backend running?', true);
  }
  const text = await res.text();
  let body: unknown = undefined;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    /* non-JSON response */
  }
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string; retryable?: boolean } } | undefined)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'HTTP_ERROR',
      err?.message ?? `Request failed with status ${res.status}`,
      err?.retryable ?? res.status >= 500,
    );
  }
  return body as T;
}

const qs = (params: Record<string, string | number | undefined | string[]>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue;
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export const api = {
  publicInfo: () => request<PublicInfo>('/public/info'),
  me: () => request<{ user: User }>('/auth/me').then((r) => r.user),
  signUp: (body: { name: string; email: string; password: string }) =>
    request<{ user: User }>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.user),
  signIn: (body: { email: string; password: string }) =>
    request<{ user: User }>('/auth/signin', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.user),
  signOut: () => request<void>('/auth/signout', { method: 'POST' }),
  config: () => request<AppConfigStatus>('/config'),
  stats: () => request<DashboardStats>('/stats'),
  pullRequests: () => request<{ items: PullRequestSummary[] }>('/pull-requests'),
  listReviews: (p: ReviewListParams) => request<Paginated<ReviewSummary>>(`/reviews${qs({ ...p })}`),
  getReview: (id: string) => request<{ review: Review }>(`/reviews/${encodeURIComponent(id)}`),
  createReview: (body: { prUrl: string; depth: ReviewDepth; focus?: string }) =>
    request<{ review: ReviewSummary; duplicate: boolean }>('/reviews', { method: 'POST', body: JSON.stringify(body) }),
  retryReview: (id: string) => request<{ review: ReviewSummary }>(`/reviews/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
  findings: (id: string) => request<{ items: Finding[] }>(`/reviews/${encodeURIComponent(id)}/findings`),
  updateFinding: (id: string, body: { status: FindingStatus; dismissReason?: string }) =>
    request<{ finding: Finding }>(`/findings/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  previewComments: (reviewId: string, findingIds: string[]) =>
    request<{ comments: CommentPreview[] }>(`/reviews/${encodeURIComponent(reviewId)}/publish-preview`, {
      method: 'POST',
      body: JSON.stringify({ findingIds }),
    }),
  publishComments: (reviewId: string, findingIds: string[]) =>
    request<PublishResult>(`/reviews/${encodeURIComponent(reviewId)}/publish-comments`, {
      method: 'POST',
      body: JSON.stringify({ findingIds, confirm: true }),
    }),
};
